<template>
    <v-form class="pb-4">
        <v-row v-if="loading || isLoading" dense>
            <v-sheet class="full-width d-flex justify-center py-2">
                <v-progress-linear color="primary" indeterminate class="mb-0" height="3px" />
            </v-sheet>
        </v-row>
        <template v-else>
            <v-row dense class="mb-4">
                <v-col cols="12" sm="6" md="2">
                    <LocaleSwitcher @change="changeActiveLangCode" />
                </v-col>
            </v-row>
            <v-row dense>
                <v-col cols="12" md="2">
                    <span class="text-h6 text--secondary">
                        {{ $t('description') }}
                    </span>
                </v-col>
                <v-col cols="12" md="10">
                    <v-row v-if="isShowEditors && descriptions?.length && !isLoading" no-gutters class="px-1">
                        <v-col v-for="{description, type} in descriptions" :key="type" cols="12" class="mb-2">
                            <TextareaLocale
                                v-model="description[activeLangCode]"
                                :label="getAliasDescriptionName(type)"
                                rows="7"
                                class="mb-2"
                            />
                        </v-col>
                    </v-row>
                </v-col>
            </v-row>
            <v-row dense class="px-3 pb-4">
                <v-btn
                    color="primary"
                    class="save-btn mr-4"
                    :loading="isSaving"
                    :disabled="isLoading || isSaving"
                    @click="save"
                >
                    {{ $t('save') }}
                </v-btn>
                <v-btn
                    class="cancel-btn"
                    color="white"
                    data-cy="cancel"
                    :disabled="isLoading || isSaving"
                    :to="{name: 'supplierProducts', hash: '#activities'}"
                >
                    {{ $t('cancel') }}
                </v-btn>
            </v-row>
        </template>
    </v-form>
</template>

<script setup lang="ts">
    import {clone} from '~/utils/helpers'

    const isLoading = ref<boolean>(false)
    const isSaving = ref<boolean>(false)
    const activeLangCode = ref<string>('en')
    provide('activeLangCode', activeLangCode)
    const isShowEditors = ref<boolean>(false)

    const excursion: Ref = ref({})
    const descriptions = ref([])
    const descriptionTypes = ref([])

    const app = useNuxtApp()
    const {t} = useI18n()

    const props = defineProps<{
        excursionInfo: any
        loading?: boolean
    }>()

    const emit = defineEmits<{
        (e: 'updated'): void
    }>()

    const changeActiveLangCode = (code: string): void => {
        activeLangCode.value = code
        flickEditors()
    }

    const initDescriptions = () => {
        const baseDescriptions = descriptionTypes.value.map(item => ({type: item, description: {}}))
        descriptions.value = excursion.value.descriptions
            ? baseDescriptions.map(base => {
                  const match = excursion.value.descriptions.find(item => item.type.code === base.type.code)
                  if (match) {
                      return {...base, description: match.description}
                  }
                  return base
              })
            : baseDescriptions
    }

    watch(
        () => props.excursionInfo,
        () => {
            excursion.value = clone(props.excursionInfo)
            initDescriptions()
        },
        {immediate: true}
    )

    onMounted(async () => {
        activeLangCode.value = app.$i18n.locale.value
        await loadDescriptionTypes()
    })

    async function loadDescriptionTypes(): Promise<void> {
        try {
            isLoading.value = true
            const response = await app.$api.excursionDescriptionTypes.get()

            const descriptionsOrder = {SHR: 1, GEN: 2}
            descriptionTypes.value = response.sort((a, b) => {
                const aLocation = descriptionsOrder[a.code] || 3
                const bLocation = descriptionsOrder[b.code] || 3

                return aLocation - bLocation
            })

            initDescriptions()
        } catch (e) {
            app.$toast.error(t('error_message.load_error'))
        } finally {
            isLoading.value = false
        }
    }

    const save = async (): Promise<void> => {
        try {
            isSaving.value = true
            const data = clone(excursion.value)

            const resultDescriptions = []

            descriptions.value.forEach(item => {
                const needToSave =
                    item.description?.[activeLangCode.value] ||
                    props.excursionInfo.descriptions.find(oldDescription => oldDescription.type.code === item.type.code)

                if (needToSave) {
                    resultDescriptions.push(item)
                }
            })

            await app.$api.excursions.put({...data, descriptions: resultDescriptions})
            emit('updated')
            app.$toast.success(t('success_message.saved_successfully'))
        } catch (e) {
            app.$toast.error(t('error_message.save_error'))
        } finally {
            isSaving.value = false
        }
    }

    const flickEditors = (): void => {
        isShowEditors.value = false
        nextTick().then(() => {
            isShowEditors.value = true
        })
    }

    watch(descriptions, async () => {
        if (descriptions.value.length) {
            flickEditors()
        }
    })

    const getAliasDescriptionName = type => {
        switch (type.code) {
            case 'GEN':
                return t('description_type.full')
            case 'SHR':
                return t('description_type.short')
            case 'SUB':
                return t('description_type.supplier_name')
            default:
                return type.name
        }
    }
</script>
